import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchTranslations } from "../types/translationsClients";
import DocklineFX from "../components/DocklineFX";
import HeroFX from "../components/HeroFX";
import ContactComponent from "../components/ContactComponent";
import { Helmet } from 'react-helmet-async';

const ItAdministratorMuenchen = () => {
    const [dict, setDict] = useState<Record<string, string> | null>(null);
    const locale = "de";
    const namespace = "landing";

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchTranslations({
                    baseUrl: process.env.REACT_APP_API_BASE_URL || "",
                    locale,
                    namespace
                });
                if (!cancelled) setDict(data.items);
            } catch (e) {
                if (!cancelled) setDict({});
                console.error("Failed to load translations (landing)", e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Reuse the hero parallax / tilt logic
    useEffect(() => {
        const hero = document.querySelector('.landing-hero-cover') as HTMLElement | null;
        if (!hero) return;

        const clamp = (v: number, min = 0, max = 1) => Math.min(max, Math.max(min, v));

        let rafId = 0;
        const loop = () => {
            const doc = document.documentElement;
            const st = doc.scrollTop || document.body.scrollTop || 0;

            const maxScroll = Math.max(1, doc.scrollHeight - doc.clientHeight);
            const hideThreshold = 0.5 * maxScroll;

            if (st >= hideThreshold) {
                hero.classList.add('lh-hidden');
            } else {
                hero.classList.remove('lh-hidden');

                const vh = window.innerHeight || 1;
                const t = clamp(st / (vh * 1.4));

                const scale = 1 - 0.20 * t;
                const tilt = 6 * t;

                hero.style.transform = `translateX(-50%) perspective(1000px) scale(${scale}) rotateX(${tilt}deg)`;
            }

            rafId = requestAnimationFrame(loop);
        };

        rafId = requestAnimationFrame(loop);
        return () => {
            hero.classList.remove('lh-hidden');
            cancelAnimationFrame(rafId);
        };
    }, []);

    return (
        <>
            <Helmet>
                <title>IT-Administrator in München | Dockline Systems</title>
                <meta
                    name="description"
                    content="Ihr IT-Administrator in München: Dockline Systems betreut Netzwerke, Arbeitsplätze, Security und Support für Unternehmen und Privatkunden in und um München."
                />
                <meta
                    name="keywords"
                    content="IT Administrator München, IT-Administrator München, IT Service München, IT Support München, Netzwerkbetreuung München, IT Dienstleister München, Dockline Systems"
                />
                <link rel="canonical" href="https://dockline.digital/it-administrator-muenchen" />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content="IT-Administrator in München – Dockline Systems" />
                <meta
                    property="og:description"
                    content="IT-Administration, Netzwerkmanagement und IT-Support in München. Dockline Systems – persönlicher IT-Administrator für Unternehmen und Privatkunden."
                />
                <meta property="og:url" content="https://dockline.digital/it-administrator-muenchen" />
                <meta property="og:image" content="https://dockline.digital/pics/og/systems-og.jpg" />
                <meta property="og:locale" content="de_DE" />

                {/* JSON-LD schema: dedicated IT service landing page for Munich */}
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "ITService",
                  "name": "IT-Administrator in München – Dockline Systems",
                  "url": "https://dockline.digital/it-administrator-muenchen",
                  "logo": "https://dockline.digital/pics/logo/sec/dsystems_logo_sec.svg",
                  "serviceType": "IT-Administration, IT-Support und Netzwerkbetreuung in München",
                  "description": "Dockline Systems bietet als IT-Administrator in München IT-Administration, Netzwerkmanagement, Security und IT-Support für Unternehmen und Privatkunden.",
                  "areaServed": [
                    {
                      "@type": "City",
                      "name": "München",
                      "addressCountry": "DE"
                    },
                    {
                      "@type": "Country",
                      "name": "Deutschland"
                    }
                  ],
                  "provider": {
                    "@type": "Organization",
                    "name": "Dockline Digital"
                  }
                }
                `}</script>

                {/* JSON-LD FAQ schema */}
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  "mainEntity": [
                    {
                      "@type": "Question",
                      "name": "Was macht ein IT-Administrator in München konkret für mein Unternehmen?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Als IT-Administrator in München betreuen wir Ihre Arbeitsplätze, Server, Netzwerke, Security-Einstellungen und Backups. Ziel ist, dass Computer, WLAN, Drucker und Anwendungen stabil laufen, Ausfälle vermieden werden und Sie einen festen Ansprechpartner für IT-Fragen haben."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Arbeiten Sie nur mit Firmen oder auch mit Privatkunden?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Wir betreuen kleine und mittlere Unternehmen, Praxen, Kanzleien und Selbständige im Raum München ebenso wie Privatkunden. Die IT-Betreuung wird jeweils an die Größe und Situation angepasst – vom einzelnen Homeoffice-Arbeitsplatz bis zum vollständigen Büro."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Bieten Sie Remote-Support, Vor-Ort-Service oder beides an?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Wir unterstützen Sie sowohl per Remote-Support als auch direkt vor Ort in München und Umgebung. Viele Probleme lassen sich schnell und sicher per Fernwartung lösen, bei Themen wie Hardware, Verkabelung, WLAN-Ausleuchtung oder Standortvernetzung kommen wir persönlich vorbei."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Wie werden IT-Administration und IT-Support abgerechnet?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "IT-Administration und IT-Support werden in der Regel nach tatsächlich angefallenen Arbeitsstunden abgerechnet. In einem ersten Gespräch klären wir Ihre Situation und geben eine realistische Einschätzung des Aufwands, damit Sie die zu erwartende Größenordnung kennen, bevor wir starten."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Können Sie eine bestehende IT-Betreuung von einem anderen Dienstleister übernehmen?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Ja. Wir können eine bestehende IT-Umgebung von einem bisherigen Dienstleister übernehmen, die aktuelle Infrastruktur dokumentieren und schrittweise verbessern. Wichtig ist eine saubere Übergabe mit Zugriffen und Dokumentation, damit der laufende Betrieb möglichst ohne Unterbrechung weiterläuft."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Unterstützen Sie auch Homeoffice-Setups und kleine Standorte rund um München?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Wir unterstützen sowohl zentrale Büros als auch Homeoffice-Arbeitsplätze und kleinere Außenstandorte. Typische Themen sind stabile VPN-Verbindungen, sicheres WLAN, Zugriff auf Server oder Cloud-Dienste und sinnvolle Backup-Konzepte für mobiles Arbeiten."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Wie schnell reagieren Sie, wenn ein akutes Problem auftritt?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Die Reaktionszeit hängt von der Dringlichkeit und dem vereinbarten Betreuungsmodell ab. In der Regel versuchen wir, akute Störungen für laufende Kundinnen und Kunden so schnell wie möglich zu bearbeiten und zumindest eine erste Einschätzung am selben Werktag zu geben."
                      }
                    }
                  ]
                }
                `}</script>
            </Helmet>

            <div style={{'--fx-color': '#1BA27A', '--fx-glow': 'rgba(27,162,122,0.35)'} as React.CSSProperties}>
                <div className="landing-hero-cover hero-anim">
                    <img src="/pics/landingPref2Systems.webp" alt="" className="landing-hero" id="landing-hero" />
                    <HeroFX/>
                    <div className="landing-hero-centered-text">
                        <h1 className="systems-color">IT-Administrator in München</h1>
                        <h2>Dockline Systems – IT-Administration &amp; Support vor Ort</h2>
                    </div>
                    <a
                        className="landing-hero-bottom-hint"
                        onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
                        style={{ cursor: 'pointer' }}
                    >
                        Scroll to Explore
                    </a>
                </div>

                <div className="content">
                    <DocklineFX color="#1BA27A"/>

                    {/* Intro tailored to Munich */}
                    <section className="systems-intro">
                        <div className="section-intro">
                            <h2 className="systems-color">IT-Administration für München und Umgebung</h2>
                            <p>
                                Dockline Systems ist Ihr persönlicher <strong>IT-Administrator in München</strong> für
                                <strong> IT-Administration, IT-Support und Netzwerkbetreuung</strong>. Wir betreuen kleine und
                                mittlere Unternehmen, Praxen und Privatkunden – inklusive <strong>IT-Beratung</strong>,
                                <strong>WLAN Einrichtung</strong>, <strong>Datensicherung</strong>,
                                <strong>Backup Service</strong> und vollständiger <strong>IT-Betreuung in München</strong>.
                            </p>
                        </div>
                    </section>

                    {/* Service grid reused, but copy localized */}
                    <section className="systems-grid" aria-labelledby="systems-grid-title">
                        <div className="section-intro">
                            <h2 id="systems-grid-title">Was wir in München übernehmen</h2>
                            <p>
                                Aktuelle Kunden sitzen in und um München – darunter Beratungen, Kanzleien und
                                medizinische Einrichtungen. Wir kümmern uns darum, dass Ihr Alltag nicht von IT-Problemen
                                bestimmt wird.
                            </p>
                        </div>
                        <div className="systems-grid__cards">
                            <article className="systems-card">
                                <i className="bi bi-hdd-network"></i>
                                <h3>Netzwerk &amp; Standortvernetzung</h3>
                                <p>
                                    Planung, Aufbau und Optimierung von LAN, WLAN und Standortvernetzungen in München – ideal
                                    für <strong>Netzwerk-installationen in Büros</strong>, stabile Performance und
                                    wachsende Anforderungen. Auch <strong>WLAN Probleme zuhause lösen</strong> wir schnell und zuverlässig.
                                </p>
                            </article>

                            <article className="systems-card">
                                <i className="bi bi-shield-lock"></i>
                                <h3>Security &amp; Compliance</h3>
                                <p>
                                    Endpoint-Schutz, Updates, Rechtekonzepte und DSGVO-nahe Einstellungen – inklusive
                                    <strong> Patch Management</strong> und <strong>Security Beratung</strong> für Unternehmen
                                    jeder Größe in München.
                                </p>
                            </article>

                            <article className="systems-card">
                                <i className="bi bi-person-gear"></i>
                                <h3>Managed IT-Support</h3>
                                <p>
                                    Laufende Betreuung Ihrer Arbeitsplätze in München: Monitoring, Wartung, Remote-Support
                                    und schnelle Hilfe bei Problemen wie <strong>Server Down</strong>,
                                    <strong> langsamen Laptops</strong> oder allgemeinen <strong>IT-Störungen</strong>.
                                </p>
                            </article>

                            <article className="systems-card">
                                <i className="bi bi-house-gear"></i>
                                <h3>Private IT-Hilfe in München</h3>
                                <p>
                                    Unterstützung für Privatkunden im Raum München – <strong>Computerhilfe</strong>,
                                    <strong> Office PC einrichten lassen</strong>, Virenentfernung, WLAN‑Optimierung und
                                    allgemeine IT‑Unterstützung für alle, die jemanden brauchen, der sich auskennt.
                                </p>
                            </article>
                        </div>
                    </section>

                    {/* Process section, slightly localized */}
                    <section className="systems-process" aria-labelledby="systems-process-title">
                        <div className="section-intro">
                            <h2 id="systems-process-title">So arbeiten wir als Ihr IT-Administrator</h2>
                            <p>
                                Ob einmalige Stabilisierung oder laufende Betreuung – wir gehen strukturiert vor und
                                sprechen in klarer, verständlicher Sprache, nicht in Buzzwords.
                            </p>
                        </div>
                        <ol className="process-steps">
                            <li className="process-step">
                                <div className="step-badge">1</div>
                                <h3>Bestandsaufnahme vor Ort oder remote</h3>
                                <p>
                                    Wir analysieren Ihre aktuelle IT-Landschaft in München: Netzwerk, Hardware, Software,
                                    Sicherheitsstatus. Ideal für <strong>kleine Unternehmen</strong>,
                                    <strong> Selbständige</strong> und Praxen, die eine strukturierte
                                    <strong>IT-Betreuung in München</strong> benötigen.
                                </p>
                            </li>
                            <li className="process-step">
                                <div className="step-badge">2</div>
                                <h3>Stabilisieren &amp; Absichern</h3>
                                <p>
                                    Wir beheben akute Probleme, schließen Sicherheitslücken und bringen Systeme auf einen
                                    stabilen, aktuellen Stand.
                                </p>
                            </li>
                            <li className="process-step">
                                <div className="step-badge">3</div>
                                <h3>Automatisieren &amp; Überwachen</h3>
                                <p>
                                    Monitoring, Backups, Patch-Management – wir automatisieren so viel wie sinnvoll ist,
                                    damit Störungen gar nicht erst entstehen.
                                </p>
                            </li>
                            <li className="process-step">
                                <div className="step-badge">4</div>
                                <h3>Langfristige Betreuung</h3>
                                <p>
                                    Als dauerhafter IT-Administrator in München stehen wir bereit – mit klaren
                                    Ansprechpartnern, Reports und unkomplizierter Kommunikation.
                                </p>
                            </li>
                        </ol>
                    </section>

                    {/* === Why Dockline Systems – IT-Administrator in München === */}
                    <section className="systems-why" aria-labelledby="itadmin-why-title">
                        <div className="section-intro">
                            <h2 id="itadmin-why-title" className="systems-color">
                                Warum Dockline Systems als IT-Administrator in München?
                            </h2>
                            <p>
                                Dockline Systems verbindet persönliche Betreuung mit einer schlanken, modernen Arbeitsweise.
                                Sie haben einen festen IT-Ansprechpartner in München, der Ihre Umgebung kennt und nicht nur
                                ein Ticket in einem anonymen System bearbeitet.
                            </p>
                        </div>
                        <div className="systems-why__grid">
                            <article className="systems-card systems-why__item">
                                <h3>Persönlich, direkt und verständlich</h3>
                                <p>
                                    Sie sprechen immer mit derselben Person – ohne Callcenter, ohne Weiterleitung durch mehrere
                                    Ebenen. Technische Themen werden verständlich erklärt, damit Sie Entscheidungen sicher und
                                    auf Augenhöhe treffen können.
                                </p>
                            </article>
                            <article className="systems-card systems-why__item">
                                <h3>Vor Ort in München &amp; schneller Remote-Support</h3>
                                <p>
                                    Viele Probleme lassen sich per sicherer Fernwartung innerhalb kurzer Zeit lösen. Wenn
                                    Verkabelung, WLAN, Hardware oder ein kompletter Büroaufbau anstehen, kommen wir als
                                    <strong> IT-Administrator in München</strong> persönlich vorbei – in der Stadt und im Umland.
                                </p>
                            </article>
                            <article className="systems-card systems-why__item">
                                <h3>Schlanke Struktur, faire Stundensätze</h3>
                                <p>
                                    Als selbstständiger IT-Administrator und Student mit mehreren Jahren Praxiserfahrung in der
                                    IT-Betreuung arbeite ich ohne großen Overhead. So erhalten Sie professionelle
                                    <strong> IT-Betreuung in München</strong> mit
                                    <em> transparenten, fairen Stundensätzen</em> – ideal für kleine Unternehmen, Praxen,
                                    Kanzleien und Privatkunden.
                                </p>
                            </article>
                        </div>
                    </section>

                    {/* === FAQ === */}
                    <section className="systems-faq" aria-labelledby="itadmin-faq-title">
                        <div className="section-intro">
                            <h2 id="itadmin-faq-title" className="systems-color">Häufige Fragen zur IT-Administration in München</h2>
                            <p>
                                Antworten auf typische Fragen rund um IT-Administration, IT-Service und IT-Betreuung in München.
                                Wenn Ihre Situation hier nicht vorkommt, schreiben Sie uns kurz – wir melden uns mit einer
                                Einschätzung.
                            </p>
                        </div>
                        <div className="faq-list">
                            <details className="faq-item">
                                <summary><p className="boldText">Was macht ein IT-Administrator in München konkret für mein Unternehmen?</p></summary>
                                <p>
                                    Als IT-Administrator in München betreuen wir Ihre Arbeitsplätze, Server, Netzwerke,
                                    Security-Einstellungen und Backups. Ziel ist, dass Computer, WLAN, Drucker und Anwendungen
                                    stabil laufen, Ausfälle vermieden werden und Sie einen festen Ansprechpartner für IT-Fragen
                                    haben.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Arbeiten Sie nur mit Firmen oder auch mit Privatkunden?</p></summary>
                                <p>
                                    Wir betreuen kleine und mittlere Unternehmen, Praxen, Kanzleien und Selbständige im Raum
                                    München ebenso wie Privatkunden. Die IT-Betreuung wird an Ihre Größe angepasst – vom
                                    einzelnen Homeoffice-Arbeitsplatz bis zum vollständigen Büro.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Bieten Sie Remote-Support, Vor-Ort-Service oder beides an?</p></summary>
                                <p>
                                    Wir unterstützen Sie sowohl per Remote-Support als auch direkt vor Ort in München und
                                    Umgebung. Viele Probleme lassen sich schnell und sicher per Fernwartung lösen, bei Themen
                                    wie Hardware, Verkabelung, WLAN-Ausleuchtung oder Standortvernetzung kommen wir persönlich
                                    vorbei.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Wie werden IT-Administration und IT-Support abgerechnet?</p></summary>
                                <p>
                                    IT-Administration und IT-Support werden in der Regel nach tatsächlich angefallenen
                                    Arbeitsstunden abgerechnet. In einem ersten Gespräch klären wir Ihre Situation und geben
                                    eine realistische Einschätzung des Aufwands, damit Sie die zu erwartende Größenordnung
                                    kennen, bevor wir starten.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Können Sie eine bestehende IT-Betreuung von einem anderen Dienstleister übernehmen?</p></summary>
                                <p>
                                    Ja. Wir können eine bestehende IT-Umgebung von einem bisherigen Dienstleister übernehmen,
                                    die aktuelle Infrastruktur dokumentieren und schrittweise verbessern. Wichtig ist eine
                                    saubere Übergabe mit Zugriffen und Dokumentation, damit der laufende Betrieb möglichst
                                    ohne Unterbrechung weiterläuft.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Unterstützen Sie auch Homeoffice-Setups und kleine Standorte rund um München?</p></summary>
                                <p>
                                    Wir unterstützen sowohl zentrale Büros als auch Homeoffice-Arbeitsplätze und kleinere
                                    Außenstandorte. Typische Themen sind stabile VPN-Verbindungen, sicheres WLAN, Zugriff auf
                                    Server oder Cloud-Dienste und sinnvolle Backup-Konzepte für mobiles Arbeiten.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Wie schnell reagieren Sie, wenn ein akutes Problem auftritt?</p></summary>
                                <p>
                                    Die Reaktionszeit hängt von der Dringlichkeit und dem vereinbarten Betreuungsmodell ab.
                                    In der Regel versuchen wir, akute Störungen für laufende Kundinnen und Kunden so schnell
                                    wie möglich zu bearbeiten und zumindest eine erste Einschätzung am selben Werktag zu
                                    geben.
                                </p>
                            </details>
                        </div>
                    </section>

                    {/* CTA pointing to contact + main Systems page */}
                    <section className="systems-cta">
                        <h2 className="systems-color">IT-Administrator in München gesucht? IT-Service &amp; Support vom lokalen IT-Dienstleister</h2>
                        <p id="systems-cta-p">
                            Ob <strong>IT-Service in München Innenstadt</strong>, oder umfassende
                            <strong> IT-Betreuung für kleine Unternehmen</strong> – wir helfen schnell, persönlich und zuverlässig.
                        </p>
                        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", justifyContent: "center" }}>
                            <Link to="/" state={{scrollTo: 'contact-section'}}>
                                <button><p>Kontakt aufnehmen</p></button>
                            </Link>
                            <Link to="/systems">
                                <button><p>Mehr zu Dockline Systems</p></button>
                            </Link>
                        </div>
                    </section>

                    {/* Optional: reuse your global contact component as a strong local CTA */}
                    {/*<section className="landing-cta-container">*/}
                    {/*    <ContactComponent />*/}
                    {/*</section>*/}
                </div>
            </div>
        </>
    );
};

export default ItAdministratorMuenchen;